import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import pb from '../lib/pocketbase';

function SearchModal({ onClose }) {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageResults, setPageResults] = useState([]);
  const [navResults, setNavResults] = useState([]);
  const [refResults, setRefResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1); // flat list navigation
  const inputRef = useRef(null);
  const debTimer = useRef(null);
  const nav = useNavigate();

  const staticPages = useMemo(
    () => [
      { title: 'Accueil', path: '/' },
      { title: 'Services', path: '/services' },
      { title: 'Métiers', path: '/metiers' },
      { title: 'Références', path: '/references' },
      { title: 'Recrutement', path: '/recrutement' },
      { title: 'Société', path: '/societe' },
      { title: 'Contact', path: '/contact' },
    ],
    []
  );

  // Sous‑menus et sommaires (anchors) à indexer
  const navTerms = useMemo(
    () => [
      // Services
      { title: 'Services – Conseil', path: '/services#conseil-details' },
      { title: 'Services – Réalisation & intégration', path: '/services#realisation-details' },
      { title: 'Services – Maintenance & infogérance', path: '/services#maintenance-details' },
      { title: 'Hébergement – Présentation', path: '/services#hebergement' },
      { title: 'Hébergement – Mutualisé', path: '/services#mutualise' },
      { title: 'Hébergement – Dédié', path: '/services#dedie' },
      { title: 'Solutions – Présentation', path: '/services#solutions' },
      { title: 'Solutions GED ELO Digital', path: '/services#elo' },
      { title: 'NuméDoc', path: '/services#numedoc' },
      // Métiers
      { title: 'Métiers – Architecture', path: '/metiers#architecture' },
      { title: 'Métiers – Développement', path: '/metiers#developpement' },
      { title: 'Métiers – Réseau & exploitation', path: '/metiers#reseau' },
      { title: 'Métiers – Design & ergonomie', path: '/metiers#design' },
      { title: 'Métiers – Content management', path: '/metiers#content' },
      { title: 'Métiers – GED', path: '/metiers#ged-metiers' },
      { title: 'Métiers – Mobilité', path: '/metiers#mobilite-metiers' },
      { title: 'Métiers – Accessibilité', path: '/metiers#accessibilite' },
      { title: 'Expertise – Mobilité', path: '/metiers#expertise-mobilite' },
      { title: 'Expertise – .NET', path: '/metiers#dotnet' },
      { title: 'Expertise – PHP', path: '/metiers#php' },
      { title: 'Expertise – Java/J2EE', path: '/metiers#java' },
      { title: 'Expertise – ColdFusion', path: '/metiers#coldfusion' },
      { title: 'Expertise – XML', path: '/metiers#xml' },
      { title: 'Expertise – ELO Digital', path: '/metiers#elo-expertise' },
      { title: 'Expertise – Panorama technique', path: '/metiers#panorama' },
      // Recrutement
      { title: 'Emplois – Web Front/Back-End', path: '/recrutement#emploi-web' },
      { title: 'Emplois – PHP', path: '/recrutement#emploi-php' },
      { title: 'Emplois – Ingénieur d’affaires', path: '/recrutement#emploi-business' },
      { title: 'Stages – Fullstack', path: '/recrutement#stage-fullstack' },
      { title: 'Stages – Assistant commercial', path: '/recrutement#stage-assistant' },
      // Société
      { title: 'Société – Qui sommes‑nous ?', path: '/societe#qui' },
      { title: 'Société – Où sommes‑nous ?', path: '/societe#ou' },
      { title: 'Société – Nous contacter', path: '/societe#contact' },
    ],
    []
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const total = pageResults.length + refResults.length;
        if (total > 0) setActiveIndex((i) => (i + 1) % total);
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const total = pageResults.length + refResults.length;
        if (total > 0) setActiveIndex((i) => (i - 1 + total) % total);
      }
      if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        const total = pageResults.length + refResults.length;
        if (!total) return;
        if (activeIndex < pageResults.length) {
          nav(pageResults[activeIndex].path);
          onClose();
        } else {
          const r = refResults[activeIndex - pageResults.length];
          if (r) {
            nav(`/references/${r.slug || r.id}`);
            onClose();
          }
        }
      }
    };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(t); };
  }, [onClose]);

  const runSearch = async (term) => {
    const s = term.trim();
    const pages = !s ? [] : staticPages.filter((p) => p.title.toLowerCase().includes(s.toLowerCase())).slice(0, 5);
    setPageResults(pages);
    const navs = !s ? [] : navTerms.filter((p) => p.title.toLowerCase().includes(s.toLowerCase())).slice(0, 8);
    setNavResults(navs);
    if (!s) { setRefResults([]); setLoading(false); return; }
    setLoading(true);
    try {
      const filter = `published=true && (client~"${s}" || titre~"${s}")`;
      const res = await pb.collection('references').getList(1, 8, { filter, sort: '-created', fields: 'id,slug,client,titre,annee' });
      setRefResults(res?.items || []);
    } catch (_) {
      setRefResults([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    clearTimeout(debTimer.current);
    debTimer.current = setTimeout(() => runSearch(q), 200);
    return () => clearTimeout(debTimer.current);
  }, [q]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (pageResults[0]) { nav(pageResults[0].path); onClose(); return; }
    if (refResults[0]) { nav(`/references/${refResults[0].slug || refResults[0].id}`); onClose(); return; }
  };

  // Utilitaire: surligner le terme recherché dans un texte (insensible à la casse)
  const renderHighlighted = (text, term) => {
    const s = String(term || '').trim();
    if (!s) return text;
    const regex = new RegExp(`(${s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
    const parts = String(text).split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? <mark key={i} className="search-highlight">{part}</mark> : <span key={i}>{part}</span>
    );
  };

  const IconPage = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
  const IconSubmenu = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18M7 12h14M11 18h10" />
    </svg>
  );
  const IconRef = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="14" rx="2" />
      <path d="M7 8h10M7 12h6" />
    </svg>
  );

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="search-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={onSubmit} className="search-modal-form">
          <div className="search-input-wrap">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-3.6-3.6" />
            </svg>
            <input
              ref={inputRef}
              type="search"
              placeholder="Rechercher…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Rechercher"
            />
          </div>
        </form>
        <button
          type="button"
          className="search-close-abs"
          aria-label="Fermer la recherche"
          onClick={onClose}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        {loading ? <div className="admin-table-loading" style={{ marginTop: '0.5rem' }}>Recherche…</div> : null}

        {!loading && (pageResults.length || navResults.length || refResults.length) ? (
          <div className="search-results">
            {pageResults.length > 0 && (
              <div>
                <div className="refs-label">Pages</div>
                <ul className="list">
                  {pageResults.map((p, idx) => (
                    <li key={p.path} className={activeIndex === idx ? 'search-active' : ''}>
                      <Link to={p.path} onClick={onClose}>
                        <span className="search-item">
                          <span className="search-icon"><IconPage /></span>
                          <span className="search-text">{renderHighlighted(p.title, q)}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {navResults.length > 0 && (
              <div>
                <div className="refs-label">Sous‑menus</div>
                <ul className="list">
                  {navResults.map((p, nidx) => (
                    <li key={p.path} className={activeIndex === (pageResults.length + nidx) ? 'search-active' : ''}>
                      <Link to={p.path} onClick={onClose}>
                        <span className="search-item">
                          <span className="search-icon"><IconSubmenu /></span>
                          <span className="search-text">{renderHighlighted(p.title, q)}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {refResults.length > 0 && (
              <div>
                <div className="refs-label">Références</div>
                <ul className="list">
                  {refResults.map((r, ridx) => (
                    <li key={r.id} className={activeIndex === (pageResults.length + navResults.length + ridx) ? 'search-active' : ''}>
                      <Link to={`/references/${r.slug || r.id}`} onClick={onClose}>
                        <span className="search-item">
                          <span className="search-icon"><IconRef /></span>
                          <span className="search-text">{renderHighlighted(`${r.client || r.titre}${r.annee ? ` • ${r.annee}` : ''}`, q)}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (!loading && q.trim() ? (
          <div className="search-results" style={{ color: 'var(--text-muted)', marginTop: '0.75rem' }}>
            Aucun résultat
          </div>
        ) : null)}
      </div>
    </div>
  );
}

export default SearchModal;
